import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject, interval, EMPTY, from } from 'rxjs';
import { takeUntil, switchMap, catchError, timeout, map } from 'rxjs/operators';
import { Document } from '../../models/document';
import { AutoclassifierSdkService } from '../autoclassifier-sdk/autoclassifier-sdk.service';
import { AuthService } from '../auth/auth.service';
import {
  AnalysisConfig,
  AnalysisStatus,
  ExtractionStatus,
  DocumentExtractionState,
  DEFAULT_ANALYSIS_CONFIG
} from './document-analysis.models';

@Injectable({
  providedIn: 'root'
})
export class DocumentAnalysisService implements OnDestroy {
  private autoclassifierSdkService = inject(AutoclassifierSdkService);
  private authService = inject(AuthService);

  private extractionStatus$ = new BehaviorSubject<Map<string, DocumentExtractionState>>(new Map());
  private analysisStatus$ = new BehaviorSubject<AnalysisStatus>('idle');
  private config$ = new BehaviorSubject<AnalysisConfig>(DEFAULT_ANALYSIS_CONFIG);

  /** Observable of the overall service status */
  readonly analysisStatus: Observable<AnalysisStatus> = this.analysisStatus$.asObservable();

  /** Observable of all extraction statuses */
  readonly extractionStatuses: Observable<Map<string, DocumentExtractionState>> =
    this.extractionStatus$.asObservable();

  private destroy$ = new Subject<void>();
  private pollingSubscription$ = new Subject<void>();
  /** Per-document cancel subjects — keyed by documentId */
  private documentCancelSubjects = new Map<string, Subject<void>>();
  private tenantId: string = '';

  constructor() {
    this.tenantId = localStorage.getItem('tenantId') || '';
  }

  /**
   * Update service configuration
   * @param config - Partial configuration to merge with current config
   */
  configure(config: Partial<AnalysisConfig>): void {
    const currentConfig = this.config$.value;
    this.config$.next({ ...currentConfig, ...config });
  }

  /**
   * Set the tenant ID for API calls
   * @param tenantId - Tenant identifier
   */
  setTenantId(tenantId: string): void {
    this.tenantId = tenantId;
  }

  /**
   * Start extraction for already-loaded documents
   * @param documents - Array of documents with content already loaded
   * @param force
   * @param documentTypeName
   * @returns Promise that resolves when extraction is initiated
   */
  async startExtraction(
    documents: Document[],
    force: boolean = false,
    documentTypeName?: string
  ): Promise<void> {
    if (!documents || documents.length === 0) {
      console.warn('[DocumentAnalysisService] No documents provided to startExtraction');
      return;
    }

    if (!this.tenantId) {
      throw new Error('[DocumentAnalysisService] Tenant ID not set. Call setTenantId() first.');
    }

    const documentsNeedingExtraction = documents.filter(doc => {
      if (doc.extractionCancelled) {
        return false;
      }

      if (doc.extractionStatus === 'extracted') {
        this.populateExistingExtractionState(doc);
        return false;
      }

      if (
        doc.extractionStatus === 'failed' ||
        doc.extractionStatus === 'extraction failed' ||
        doc.extractionStatus === 'extraction prompt failed'
      ) {
        this.populateExistingExtractionState(doc);
        return false;
      }

      return true;
    });

    if (documentsNeedingExtraction.length === 0) {
      this.analysisStatus$.next('complete');
      return;
    }

    try {
      this.analysisStatus$.next('analyzing');

      documents.forEach(doc => {
        const existing = this.documentCancelSubjects.get(doc.fileId);
        if (existing) {
          existing.next(); // cancel any prior polling for this doc
        }
        this.documentCancelSubjects.set(doc.fileId, new Subject<void>());
      });

      await Promise.all(
        documentsNeedingExtraction.map(doc => this.analyzeDocument(doc, force, documentTypeName))
      );

      const documentIds = documentsNeedingExtraction
        .filter(doc => this.isDocumentIncomplete(doc))
        .map(doc => doc.fileId);

      if (documentIds.length > 0) {
        this.startStatusPolling(documentIds);
      } else {
        this.analysisStatus$.next('complete');
      }
    } catch (error) {
      console.error('[DocumentAnalysisService] Error in startExtraction:', error);
      this.analysisStatus$.next('error');
      throw error;
    }
  }

  /**
   * Populate the extraction status map with existing document state
   * Used for documents that have already been processed (extracted or failed)
   * @param document - Document with existing extraction state
   */
  private populateExistingExtractionState(document: Document): void {
    const extractionState: DocumentExtractionState = {
      documentId: document.fileId,
      extractionStatus: document.extractionStatus as ExtractionStatus,
      extractionRecordId: document.extractionRecordId
    };

    if (document.extractionData) {
      extractionState.extractionData = document.extractionData;
    }

    if (document.status) {
      extractionState.status = document.status;
    }

    if (
      document.extractionStatus === 'failed' ||
      document.extractionStatus === 'extraction failed' ||
      document.extractionStatus === 'extraction prompt failed'
    ) {
      extractionState.error = 'Extraction failed';
    }

    const statusMap = this.extractionStatus$.value;
    statusMap.set(document.fileId, extractionState);
    this.extractionStatus$.next(new Map(statusMap));
  }

  /**
   * Get extraction status for a specific document
   * @param documentId - Document identifier
   * @returns Observable of the document's extraction state
   */
  getExtractionStatus(documentId: string): Observable<DocumentExtractionState | undefined> {
    return this.extractionStatus$.pipe(map(statusMap => statusMap.get(documentId)));
  }

  /**
   * Get all extraction statuses
   * @returns Observable of Map<documentId, DocumentExtractionState>
   */
  getAllExtractionStatuses(): Observable<Map<string, DocumentExtractionState>> {
    return this.extractionStatus$.asObservable();
  }

  /**
   * Clear state for a single document without affecting others
   * Use before re-extracting a single document
   * @param documentId - Document ID to clear
   */
  clearDocumentState(documentId: string): void {
    const statusMap = this.extractionStatus$.value;
    statusMap.delete(documentId);
    this.extractionStatus$.next(new Map(statusMap));
  }

  /**
   * Cancel extraction for specific documents
   * Stops polling for those documents and marks them as cancelled
   * @param documentIds - Array of document IDs to cancel
   */
  cancelExtraction(documentIds: string[]): void {
    const statusMap = this.extractionStatus$.value;

    documentIds.forEach(docId => {
      const cancelSubject = this.documentCancelSubjects.get(docId);
      if (cancelSubject) {
        cancelSubject.next();
        this.documentCancelSubjects.delete(docId);
      }

      const currentState = statusMap.get(docId);
      if (currentState) {
        statusMap.set(docId, {
          ...currentState,
          extractionStatus: 'failed',
          error: 'Extraction cancelled by user'
        });
      }
    });

    this.extractionStatus$.next(new Map(statusMap));

    const hasIncompleteDocuments = Array.from(statusMap.values()).some(state =>
      this.isStateIncomplete(state)
    );

    if (!hasIncompleteDocuments) {
      this.stopStatusPolling();
      this.analysisStatus$.next('complete');
    }
  }

  /**
   * Clear extraction state for specific documents.
   * Removes entries from the status map entirely so they can be re-extracted.
   * Unlike cancelExtraction, this does NOT mark them as 'failed'.
   * @param documentIds - Array of document IDs to clear
   */
  clearExtractionState(documentIds: string[]): void {
    const statusMap = this.extractionStatus$.value;
    documentIds.forEach(docId => statusMap.delete(docId));
    this.extractionStatus$.next(new Map(statusMap));
  }

  /**
   * Reset the service state
   * Clears extraction status tracking and stops polling
   * Does NOT clear documents (component responsibility)
   */
  reset(): void {
    this.stopStatusPolling();
    this.documentCancelSubjects.forEach(subject => subject.next());
    this.documentCancelSubjects.clear();
    this.analysisStatus$.next('idle');
    this.extractionStatus$.next(new Map());
  }

  /**
   * Analyze a single document
   * Starts extraction and gets initial status
   * @param document - Document to analyze
   * @param force
   * @param documentTypeName - optional Document Type name (skip classification)
   */
  private async analyzeDocument(
    document: Document,
    force: boolean = false,
    documentTypeName?: string
  ): Promise<void> {
    const authToken = this.authService.getAuthToken()!;

    try {
      const response = await this.autoclassifierSdkService.documentAnalysisSdk.startExtraction(
        document.fileId,
        this.tenantId,
        authToken,
        force,
        documentTypeName
      );

      document.extractionRecordId = response.record_id;

      const recordData =
        await this.autoclassifierSdkService.documentAnalysisSdk.getExtractionStatus(
          response.record_id,
          this.tenantId,
          authToken
        );

      const extractionStatus = recordData.record_status as ExtractionStatus;
      document.extractionStatus = extractionStatus;

      const extractionState: DocumentExtractionState = {
        documentId: document.fileId,
        extractionStatus: extractionStatus,
        extractionRecordId: response.record_id
      };

      if (
        recordData.record_status === 'extracted' ||
        recordData.record_status === 'extraction prompt failed' ||
        recordData.record_status === 'extraction failed' ||
        recordData.record_status === 'failed'
      ) {
        document.extractionData = recordData;
        document.status = {
          documents: [
            {
              documentType: recordData.ai_doctype || recordData.tenant_doctype || 'Unknown',
              confidence: recordData.classification_result?.confidence || 1.0,
              pages: []
            }
          ]
        } as any;

        extractionState.extractionData = recordData;
        extractionState.status = document.status;

        if (
          recordData.record_status === 'extraction prompt failed' ||
          recordData.record_status === 'extraction failed' ||
          recordData.record_status === 'failed'
        ) {
          extractionState.error = 'Extraction failed';
        }
      }

      const statusMap = this.extractionStatus$.value;
      statusMap.set(document.fileId, extractionState);
      this.extractionStatus$.next(new Map(statusMap));

      await this.autoclassifierSdkService.documentManagementSdk.updateDocument(
        this.tenantId,
        document.fileId,
        {
          extractionRecordId: response.record_id,
          extractionStatus: document.extractionStatus
        },
        authToken
      );
    } catch (error) {
      console.error(
        `[DocumentAnalysisService] Error analyzing document ${document.fileName}:`,
        error
      );
      document.status = { documents: [] };
      document.extractionStatus = 'failed';

      const statusMap = this.extractionStatus$.value;
      statusMap.set(document.fileId, {
        documentId: document.fileId,
        extractionStatus: 'failed',
        error: error instanceof Error ? error.message : 'Unknown error'
      });
      this.extractionStatus$.next(new Map(statusMap));

      throw error;
    }
  }

  /**
   * Start polling for extraction status updates
   * Each document gets its own independent polling loop scoped only to its
   * own cancel Subject and the service destroy$ — NOT to pollingSubscription$,
   * so re-extracting one document never tears down other documents' loops.
   * @param documentIds - Array of document IDs to poll
   */
  private startStatusPolling(documentIds: string[]): void {
    const config = this.config$.value;
    this.analysisStatus$.next('polling');

    documentIds.forEach(docId => {
      const cancelSubject = this.documentCancelSubjects.get(docId);
      if (!cancelSubject) return;

      const polling$ = interval(config.pollingInterval).pipe(
        takeUntil(cancelSubject),
        takeUntil(this.destroy$),
        switchMap(() => {
          const state = this.extractionStatus$.value.get(docId);
          if (!this.isStateIncomplete(state)) {
            cancelSubject.next();
            this.documentCancelSubjects.delete(docId);

            const anyIncomplete = Array.from(this.extractionStatus$.value.values()).some(s =>
              this.isStateIncomplete(s)
            );
            if (!anyIncomplete) {
              this.analysisStatus$.next('complete');
            }
            return EMPTY;
          }
          return from(this.checkDocumentStatuses([docId]));
        }),
        catchError(error => {
          console.error(`[DocumentAnalysisService] Polling error for ${docId}:`, error);
          return EMPTY;
        })
      );

      polling$.subscribe();
    });
  }

  /**
   * Stop status polling
   */
  private stopStatusPolling(): void {
    this.pollingSubscription$.next();
  }

  /**
   * Check status for multiple documents
   * Only emits updates when status actually changes to prevent unnecessary UI rebuilds
   * @param documentIds - Array of document IDs to check
   */
  private async checkDocumentStatuses(documentIds: string[]): Promise<void> {
    const authToken = this.authService.getAuthToken()!;
    const statusMap = this.extractionStatus$.value;
    const changedDocIds: string[] = [];

    try {
      const statusChecks = documentIds.map(async docId => {
        const currentState = statusMap.get(docId);
        if (!currentState || !currentState.extractionRecordId) {
          console.warn(
            `[DocumentAnalysisService] Cannot check status for ${docId} - extraction record ID not available`
          );
          return;
        }

        try {
          const recordData =
            await this.autoclassifierSdkService.documentAnalysisSdk.getExtractionStatus(
              currentState.extractionRecordId,
              this.tenantId,
              authToken
            );

          const extractionStatus = recordData.record_status as ExtractionStatus;

          if (currentState.extractionStatus === extractionStatus) {
            return;
          }

          const updatedState: DocumentExtractionState = {
            ...currentState,
            extractionStatus: extractionStatus
          };

          if (
            recordData.record_status === 'extracted' ||
            extractionStatus === 'extraction prompt failed' ||
            extractionStatus === 'extraction failed' ||
            extractionStatus === 'failed'
          ) {
            updatedState.extractionData = recordData;
            updatedState.status = {
              documents: [
                {
                  documentType: recordData.ai_doctype || recordData.tenant_doctype || 'Unknown',
                  confidence: recordData.classification_result?.confidence || 1.0,
                  pages: []
                }
              ]
            } as any;

            if (
              extractionStatus === 'extraction prompt failed' ||
              extractionStatus === 'extraction failed' ||
              extractionStatus === 'failed'
            ) {
              updatedState.error = 'Extraction failed';
            }
          }

          statusMap.set(docId, updatedState);
          changedDocIds.push(docId);
        } catch (error) {
          console.error(`[DocumentAnalysisService] Error checking status for ${docId}:`, error);

          statusMap.set(docId, {
            ...currentState,
            extractionStatus: 'failed',
            error: error instanceof Error ? error.message : 'Unknown error'
          });
          changedDocIds.push(docId);
        }
      });

      await Promise.all(statusChecks);

      if (changedDocIds.length > 0) {
        this.extractionStatus$.next(new Map(statusMap));
      }
    } catch (error) {
      console.error('[DocumentAnalysisService] Error checking document statuses:', error);
    }
  }

  /**
   * Check if a document's extraction is incomplete
   * @param document - Document to check
   * @returns true if extraction is still in progress
   */
  private isDocumentIncomplete(document: Document): boolean {
    if (!document.extractionStatus) return false;
    return this.isStatusIncomplete(document.extractionStatus as ExtractionStatus);
  }

  /**
   * Check if an extraction status indicates incomplete processing
   * @param status - Extraction status to check
   * @returns true if status indicates processing is ongoing
   */
  private isStatusIncomplete(status: ExtractionStatus | undefined): boolean {
    if (!status) return false;

    const processingStatuses: ExtractionStatus[] = [
      'pending',
      'text_extracted',
      'ocr_complete',
      'classified',
      'generating extraction prompt',
      'extract prompt successful',
      'starting classification'
    ];

    return processingStatuses.includes(status);
  }

  /**
   * Check if an extraction state indicates incomplete processing
   * @param state - Extraction state to check
   * @returns true if status indicates processing is ongoing
   */
  private isStateIncomplete(state: DocumentExtractionState | undefined): boolean {
    if (!state) return false;
    return this.isStatusIncomplete(state.extractionStatus);
  }

  /**
   * Clean up
   */
  ngOnDestroy(): void {
    this.stopStatusPolling();
    this.documentCancelSubjects.forEach(subject => subject.next());
    this.documentCancelSubjects.clear();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
